import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, ScatterPlot, Scatter } from 'recharts';
import { TrendingUp, Users, MapPin, Target, Building2, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export default function Analytics() {
  const [donorAnalysis, setDonorAnalysis] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [loading, setLoading] = useState(true);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch donor analysis
      const donorResponse = await axios.get(`${API}/donors/analysis`);
      setDonorAnalysis(donorResponse.data);
      
      // Fetch organizations for geographic and mission analysis
      const orgResponse = await axios.get(`${API}/organizations?economic_dev_only=true&per_page=1000`);
      setOrganizations(orgResponse.data.organizations || []);
      
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-80 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Process data for charts
  const topDonorsData = donorAnalysis?.top_donors?.slice(0, 10).map((donor, index) => ({
    name: donor._id?.substring(0, 15) || 'Anonymous',
    fullName: donor._id || 'Anonymous',
    giving: donor.total_giving || 0,
    organizations: donor.organizations_count || 0,
    donations: donor.donation_count || 0
  })) || [];

  // Geographic distribution
  const geoData = organizations.reduce((acc, org) => {
    const state = org.state;
    if (state) {
      acc[state] = (acc[state] || 0) + 1;
    }
    return acc;
  }, {});
  
  const topStates = Object.entries(geoData)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([state, count]) => ({ state, organizations: count }));

  // Mission keyword analysis
  const missionKeywords = organizations.reduce((acc, org) => {
    org.mission_keywords?.forEach(keyword => {
      acc[keyword] = (acc[keyword] || 0) + 1;
    });
    return acc;
  }, {});
  
  const topKeywords = Object.entries(missionKeywords)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 8)
    .map(([keyword, count]) => ({ keyword, count }));

  // Revenue distribution
  const revenueRanges = {
    '$0-1M': 0,
    '$1-2M': 0,
    '$2-5M': 0,
    '$5-10M': 0,
    '$10M+': 0
  };
  
  organizations.forEach(org => {
    const revenue = org.total_revenue || 0;
    if (revenue < 1000000) revenueRanges['$0-1M']++;
    else if (revenue < 2000000) revenueRanges['$1-2M']++;
    else if (revenue < 5000000) revenueRanges['$2-5M']++;
    else if (revenue < 10000000) revenueRanges['$5-10M']++;
    else revenueRanges['$10M+']++;
  });
  
  const revenueData = Object.entries(revenueRanges).map(([range, count]) => ({
    range,
    count,
    percentage: organizations.length > 0 ? ((count / organizations.length) * 100).toFixed(1) : 0
  }));

  return (
    <div className="min-h-screen p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 mb-2">
          Donor & Organization Analytics
        </h1>
        <p className="text-slate-600 max-w-3xl">
          Deep insights into economic development funding patterns, donor relationships, 
          and organizational distribution. Analysis includes all donors contributing $50,000+ 
          across foundations, corporations, impact investors, and government agencies.
        </p>
      </div>

      {/* Key Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="high-contrast hover-lift transition-smooth" data-testid="unique-donors-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold data-text">
                  {donorAnalysis?.top_donors?.length || 0}
                </div>
                <div className="text-sm text-slate-600">Unique Donors</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="high-contrast hover-lift transition-smooth" data-testid="total-states-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded">
                <MapPin className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold data-text">
                  {topStates.length}
                </div>
                <div className="text-sm text-slate-600">States Covered</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="high-contrast hover-lift transition-smooth" data-testid="mission-focus-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold data-text">
                  {topKeywords.length}
                </div>
                <div className="text-sm text-slate-600">Mission Focus Areas</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="high-contrast hover-lift transition-smooth" data-testid="avg-org-revenue-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold data-text">
                  ${donorAnalysis?.organization_stats?.avg_revenue ? 
                    (donorAnalysis.organization_stats.avg_revenue / 1000000).toFixed(1) : 0}M
                </div>
                <div className="text-sm text-slate-600">Avg Revenue</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Donors by Giving */}
        <Card className="high-contrast" data-testid="top-donors-chart">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Top Donors by Total Giving
            </CardTitle>
            <p className="text-sm text-slate-600">
              All funders contributing $50K+ in economic development space
            </p>
          </CardHeader>
          <CardContent>
            {topDonorsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={topDonorsData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                    tickFormatter={(value) => `$${(value / 1000)}K`}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'giving' ? `$${value.toLocaleString()}` : value,
                      name === 'giving' ? 'Total Giving' : 'Organizations Supported'
                    ]}
                    labelFormatter={(label) => {
                      const donor = topDonorsData.find(d => d.name === label);
                      return donor?.fullName || label;
                    }}
                  />
                  <Bar dataKey="giving" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-350 flex items-center justify-center text-slate-500">
                No donor data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Geographic Distribution */}
        <Card className="high-contrast" data-testid="geographic-chart">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Geographic Distribution
            </CardTitle>
            <p className="text-sm text-slate-600">
              Organizations by state concentration
            </p>
          </CardHeader>
          <CardContent>
            {topStates.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={topStates} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="state" 
                    tick={{ fontSize: 12, fontFamily: 'JetBrains Mono' }}
                  />
                  <YAxis tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <Tooltip 
                    formatter={(value) => [value, 'Organizations']}
                    labelStyle={{ fontFamily: 'Inter' }}
                  />
                  <Bar dataKey="organizations" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-350 flex items-center justify-center text-slate-500">
                No geographic data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mission Focus Analysis */}
        <Card className="high-contrast" data-testid="mission-keywords-chart">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Mission Focus Areas
            </CardTitle>
            <p className="text-sm text-slate-600">
              Most common economic development keywords
            </p>
          </CardHeader>
          <CardContent>
            {topKeywords.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={topKeywords} layout="horizontal" margin={{ top: 20, right: 30, left: 100, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    type="number" 
                    tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="keyword" 
                    tick={{ fontSize: 11, fontFamily: 'Inter' }}
                    width={90}
                  />
                  <Tooltip 
                    formatter={(value) => [value, 'Organizations']}
                    labelStyle={{ fontFamily: 'Inter' }}
                  />
                  <Bar dataKey="count" fill={CHART_COLORS[4]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-350 flex items-center justify-center text-slate-500">
                No mission keyword data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Distribution */}
        <Card className="high-contrast" data-testid="revenue-distribution-chart">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Revenue Distribution
            </CardTitle>
            <p className="text-sm text-slate-600">
              Organization size by annual revenue ranges
            </p>
          </CardHeader>
          <CardContent>
            {revenueData.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={revenueData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="count"
                    >
                      {revenueData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value} organizations (${props.payload.percentage}%)`,
                        props.payload.range
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                
                <div className="grid grid-cols-2 gap-2">
                  {revenueData.map((item, index) => (
                    <div key={item.range} className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded" 
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      ></div>
                      <span className="text-sm data-text">
                        {item.range}: {item.count} ({item.percentage}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-300 flex items-center justify-center text-slate-500">
                No revenue data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
